const toNumber = (value) => {
  if (value === "" || value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = String(value).trim();
  // Formato AR con miles: "1.234,5" → coma decimal. Si no hay coma, el punto es decimal ("85.5").
  const normalized = s.includes(",")
    ? s.replace(/\./g, "").replace(",", ".")
    : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const mean = (arr) =>
  arr.length ? arr.reduce((sum, value) => sum + value, 0) / arr.length : 0;

const median = (arr) => {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const roundNearest = (value) => {
  const n = Number(value || 0);
  const step = Math.abs(n) >= 100000 ? 1000 : 500;
  return Math.round(n / step) * step;
};

const modeCurrency = (properties = []) => {
  const counts = {};
  properties.forEach((property) => {
    const currency = property?.price_currency || "USD";
    counts[currency] = (counts[currency] || 0) + 1;
  });
  return (
    Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "USD"
  );
};

const getIqrBounds = (values) => {
  if (values.length < 4) return { low: -Infinity, high: Infinity };
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = median(sorted.slice(0, Math.floor(sorted.length / 2)));
  const q3 = median(sorted.slice(Math.ceil(sorted.length / 2)));
  const iqr = q3 - q1;
  return {
    low: q1 - 1.5 * iqr,
    high: q3 + 1.5 * iqr,
  };
};

const AREA_FORM_KEYS = new Set(["coveredArea", "totalArea"]);

const getWizardNumber = (wizardData, formKey, wdKey) => {
  const useRange = Boolean(wizardData?.[`${formKey}UseRange`]);
  const allowDecimal = AREA_FORM_KEYS.has(formKey);
  if (useRange) {
    const min = toNumber(wizardData?.[`${formKey}Min`]);
    const max = toNumber(wizardData?.[`${formKey}Max`]);
    if (min != null && max != null) {
      const avg = (min + max) / 2;
      return allowDecimal ? avg : Math.round(avg);
    }
    return min ?? max;
  }
  return toNumber(wizardData?.[formKey] ?? wizardData?.[wdKey]);
};

const normalizeTarget = (wizardData = {}) => {
  const selectedPlace = wizardData.selectedPlace || wizardData.selected_place;
  return {
    typeProperty: wizardData.typeProperty || wizardData.type_property || "",
    rooms: getWizardNumber(wizardData, "rooms", "rooms"),
    bedrooms: getWizardNumber(wizardData, "bedrooms", "bedrooms"),
    bathrooms: getWizardNumber(wizardData, "bathrooms", "bathrooms"),
    coveredArea: getWizardNumber(
      wizardData,
      "coveredArea",
      "square_meters_covered",
    ),
    totalArea: getWizardNumber(wizardData, "totalArea", "square_meters_total"),
    parking: toNumber(wizardData.parking),
    lat: toNumber(selectedPlace?.lat),
    lng: toNumber(selectedPlace?.lng),
    amenities: {
      hasPool: Boolean(wizardData.has_pool || wizardData.hasPool),
      hasGarden: Boolean(wizardData.has_garden || wizardData.hasGarden),
      hasGrill: Boolean(wizardData.has_grill || wizardData.hasGrill),
      hasTerrace: Boolean(wizardData.has_terrace || wizardData.hasTerrace),
      hasBalcony: Boolean(wizardData.has_balcony || wizardData.hasBalcony),
    },
  };
};

const ratioScore = (target, comparable) => {
  if (!target || !comparable) return 0.6;
  const diff = Math.abs(comparable - target) / Math.max(target, 1);
  return Math.max(0, 1 - diff / 0.65);
};

const numericScore = (target, comparable, maxDiff) => {
  if (target == null || comparable == null) return 0.65;
  return Math.max(0, 1 - Math.abs(comparable - target) / maxDiff);
};

const booleanScore = (target, comparable) => {
  if (!target) return 0.8;
  return comparable ? 1 : 0.35;
};

const haversineKm = (aLat, aLng, bLat, bLng) => {
  if ([aLat, aLng, bLat, bLng].some((value) => value == null)) return null;
  const toRad = (value) => (value * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return earthKm * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const distanceScore = (distanceKm) => {
  if (distanceKm == null) return 0.65;
  if (distanceKm <= 0.5) return 1;
  if (distanceKm >= 4) return 0.2;
  return Math.max(0.2, 1 - (distanceKm - 0.5) / 3.5);
};

const computeSimilarity = (property, target) => {
  const weights = [];
  const add = (weight, score) => weights.push({ weight, score });

  const propType = property.typeProperty || property.type_property || property.type;
  if (target.typeProperty && propType) {
    add(18, propType === target.typeProperty ? 1 : 0.25);
  }

  add(18, ratioScore(target.coveredArea, toNumber(property.square_meters_covered)));
  add(14, ratioScore(target.totalArea, toNumber(property.square_meters_total)));
  add(8, numericScore(target.rooms, toNumber(property.rooms), 3));
  add(8, numericScore(target.bedrooms, toNumber(property.bedrooms), 3));
  add(6, numericScore(target.bathrooms, toNumber(property.bathrooms), 2));
  add(4, numericScore(target.parking, toNumber(property.parking), 2));

  const distanceKm = haversineKm(
    target.lat,
    target.lng,
    toNumber(property.lat),
    toNumber(property.lng),
  );
  add(13, distanceScore(distanceKm));

  add(1.5, booleanScore(target.amenities.hasPool, property.hasPool));
  add(1.5, booleanScore(target.amenities.hasGarden, property.hasGarden));
  add(1.5, booleanScore(target.amenities.hasGrill, property.hasGrill));
  add(1.5, booleanScore(target.amenities.hasTerrace, property.hasTerrace));
  add(1.5, booleanScore(target.amenities.hasBalcony, property.hasBalcony));

  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0) || 1;
  const raw =
    weights.reduce((sum, item) => sum + item.weight * item.score, 0) /
    totalWeight;

  return {
    score: Math.round(raw * 100),
    distanceKm,
  };
};

const adjustedPriceForTarget = (property, target) => {
  const price = toNumber(property.price);
  const propTotal = toNumber(property.square_meters_total);
  const propCovered = toNumber(property.square_meters_covered);

  if (target.totalArea && propTotal) {
    return {
      value: (price / propTotal) * target.totalArea,
      area: target.totalArea,
      comparableArea: propTotal,
      basis: "m2_total",
      pricePerM2: price / propTotal,
    };
  }

  if (target.coveredArea && propCovered) {
    return {
      value: (price / propCovered) * target.coveredArea,
      area: target.coveredArea,
      comparableArea: propCovered,
      basis: "m2_cubiertos",
      pricePerM2: price / propCovered,
    };
  }

  const fallbackArea = propTotal || propCovered;
  return {
    value: price,
    area: null,
    comparableArea: fallbackArea,
    basis: fallbackArea ? "precio_con_m2_ref" : "precio",
    pricePerM2: fallbackArea ? price / fallbackArea : null,
  };
};

const assessSuitability = ({ used, total, avgDays, avgSimilarity }) => {
  const countScore = Math.min(40, used * 6);
  const similarityScore = Math.min(45, avgSimilarity * 0.45);
  const agePenalty = avgDays > 180 ? 12 : avgDays > 120 ? 6 : 0;
  const confidenceScore = Math.max(
    0,
    Math.min(100, Math.round(countScore + similarityScore + 20 - agePenalty)),
  );

  if (used >= 6 && avgSimilarity >= 70 && confidenceScore >= 75) {
    return {
      level: "ok",
      label: "Estimación sólida",
      color: "#16a34a",
      confidenceScore,
    };
  }

  if (used >= 3 || (used >= 2 && avgSimilarity >= 60)) {
    return {
      level: "warn",
      label: "Estimación a revisar",
      color: "#f59e0b",
      confidenceScore,
    };
  }

  return {
    level: "bad",
    label: "Faltan comparables",
    color: "#dc2626",
    confidenceScore,
  };
};

const buildOptions = ({ recommended, market, avgDays, avgSimilarity }) => {
  const quickDiscount = avgDays > 150 ? 0.94 : avgDays > 90 ? 0.95 : 0.96;
  const premiumBoost = avgSimilarity >= 75 && avgDays < 120 ? 1.05 : 1.035;

  return [
    {
      key: "recommended",
      label: "Estimado recomendado",
      shortLabel: "Estimado",
      value: roundNearest(recommended),
      hint: "Mejor equilibrio entre precio defendible y posibilidad real de venta.",
      intent: "equilibrio",
      primary: true,
    },
    {
      key: "quick",
      label: "Venta rápida",
      shortLabel: "Rápida",
      value: roundNearest(recommended * quickDiscount),
      hint: "Útil si el objetivo principal es acelerar consultas y negociación.",
      intent: "velocidad",
    },
    {
      key: "market",
      label: "Valor defendible",
      shortLabel: "Defendible",
      value: roundNearest(market),
      hint: "Anclado en la mediana ajustada de comparables, fácil de explicar al cliente.",
      intent: "respaldo",
    },
    {
      key: "premium",
      label: "Ambicioso",
      shortLabel: "Ambicioso",
      value: roundNearest(recommended * premiumBoost),
      hint: "Para salir con margen si la propiedad tiene diferenciales claros.",
      intent: "margen",
    },
  ].filter((option) => option.value > 0);
};

export const formatRecommendationPrice = (value, currency = "USD") =>
  `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;

export function computePriceRecommendation(rawProperties = [], wizardData = {}) {
  const currency = modeCurrency(rawProperties);
  const target = normalizeTarget(wizardData);

  const comparables = rawProperties
    .filter(
      (property) =>
        toNumber(property?.price) > 0 &&
        (property?.price_currency || currency) === currency,
    )
    .map((property) => {
      const adjusted = adjustedPriceForTarget(property, target);
      const similarity = computeSimilarity(property, target);
      const days = Math.max(toNumber(property.days_published) || 0, 0);
      const views = Math.max(toNumber(property.views) || 0, 0);
      return {
        property,
        price: toNumber(property.price),
        adjustedPrice: adjusted.value,
        pricePerM2: adjusted.pricePerM2,
        areaBasis: adjusted.basis,
        targetArea: adjusted.area,
        comparableArea: adjusted.comparableArea,
        similarity: similarity.score,
        distanceKm: similarity.distanceKm,
        days,
        views,
        viewsPerDay: views / Math.max(days || 1, 1),
      };
    });

  const bounds = getIqrBounds(comparables.map((item) => item.adjustedPrice));
  const cleaned = comparables.filter(
    (item) => item.adjustedPrice >= bounds.low && item.adjustedPrice <= bounds.high,
  );

  const statsBase = {
    total: comparables.length,
    used: cleaned.length,
    removed: Math.max(comparables.length - cleaned.length, 0),
  };

  if (cleaned.length < 2) {
    return {
      currency,
      primary: null,
      options: [],
      alternatives: [],
      stats: statsBase,
      suitability: assessSuitability({
        used: cleaned.length,
        total: comparables.length,
        avgDays: 0,
        avgSimilarity: 0,
      }),
      drivers: ["Activá al menos 2 comparables similares para calcular el estimado."],
    };
  }

  const maxViewsPerDay = Math.max(...cleaned.map((item) => item.viewsPerDay), 1);
  const avgDays = mean(cleaned.map((item) => item.days));
  const avgSimilarity = mean(cleaned.map((item) => item.similarity));
  const avgPricePerM2 = mean(
    cleaned
      .map((item) => item.pricePerM2)
      .filter((value) => Number.isFinite(value) && value > 0),
  );

  const weights = cleaned.map((item) => {
    const similarityWeight = Math.max(0.2, item.similarity / 100) ** 2;
    const recencyWeight = 1 - Math.min(item.days / 365, 0.28);
    const demandWeight = 0.96 + 0.12 * (item.viewsPerDay / maxViewsPerDay);
    return similarityWeight * recencyWeight * demandWeight;
  });

  const weightSum = weights.reduce((sum, value) => sum + value, 0) || 1;
  const weightedAverage =
    cleaned.reduce(
      (sum, item, index) => sum + item.adjustedPrice * weights[index],
      0,
    ) / weightSum;
  const marketMedian = median(cleaned.map((item) => item.adjustedPrice));
  const recommended = weightedAverage * 0.68 + marketMedian * 0.32;

  const options = buildOptions({
    recommended,
    market: marketMedian,
    avgDays,
    avgSimilarity,
  });
  const primary = options.find((option) => option.primary) || options[0] || null;

  const suitability = assessSuitability({
    used: cleaned.length,
    total: comparables.length,
    avgDays,
    avgSimilarity,
  });

  const basis = cleaned.find((item) => item.targetArea)?.areaBasis || "precio";
  const drivers = [
    `${cleaned.length} comparables activos usados`,
    `${Math.round(avgSimilarity)}% de similitud promedio`,
    basis === "m2_total"
      ? "Ajustado por m² totales de la propiedad"
      : basis === "m2_cubiertos"
        ? "Ajustado por m² cubiertos de la propiedad"
        : "Calculado sobre precios publicados",
    statsBase.removed > 0
      ? `${statsBase.removed} valor atípico fuera de muestra`
      : "Sin valores atípicos relevantes",
  ];

  return {
    currency,
    primary,
    options,
    alternatives: options.filter((option) => !option.primary),
    stats: {
      ...statsBase,
      avgDays: Math.round(avgDays),
      avgSimilarity: Math.round(avgSimilarity),
      avgPricePerM2: avgPricePerM2 ? Math.round(avgPricePerM2) : null,
      weightedAverage: roundNearest(weightedAverage),
      marketMedian: roundNearest(marketMedian),
      basis,
      topComparables: cleaned
        .slice()
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 3)
        .map((item) => ({
          id: item.property.id,
          address: item.property.address || item.property.location,
          similarity: item.similarity,
          price: item.price,
          adjustedPrice: roundNearest(item.adjustedPrice),
          distanceKm: item.distanceKm,
        })),
    },
    suitability,
    drivers,
  };
}